import { FC, memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/common/Button';
import { InputField } from 'components/common/InputField';
import { useOutsideClick } from 'lib/hooks/useOutsideClick';
import { ReactComponent as Edit } from 'images/edit.svg';
import s from './ContactField.module.scss';

type ContactFieldProps = {
  title: string;
  value: string;
  type?: 'email' | 'tel';
  placeholder?: string;
  formatValue?: (value: string) => string;
  isLoading?: boolean;
  onSubmit: (value: string) => void | Promise<unknown>;
};

export const ContactField: FC<ContactFieldProps> = memo(
  ({
    title,
    value,
    type,
    placeholder,
    isLoading = false,
    formatValue,
    onSubmit,
  }) => {
    const { t } = useTranslation();
    const [isEdit, setIsEdit] = useState(false);
    const [draft, setDraft] = useState(value);
    const editRef = useRef<HTMLDivElement>(null);

    const handleCancelEdit = useCallback(() => setIsEdit(false), []);

    useOutsideClick({
      ref: editRef,
      handlerClose: handleCancelEdit,
      attached: isEdit && !isLoading,
      capture: true,
    });

    const handleStartEdit = () => {
      setDraft(value);
      setIsEdit(true);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(formatValue ? formatValue(e.target.value) : e.target.value);
    };

    const handleSubmit = async () => {
      await onSubmit(draft.trim());
      setIsEdit(false);
    };

    if (isEdit) {
      return (
        <div ref={editRef} className={s['edit']}>
          <InputField
            type={type}
            className={s['input-field']}
            title={title}
            value={draft}
            placeholder={placeholder}
            onChange={handleChange}
            autoFocus
            action={
              <Button
                className={s['confirm-btn']}
                onClick={handleSubmit}
                disabled={isLoading || !draft.trim() || draft.trim() === value}
                isAccent={true}
              >
                {t('common.confirm')}
              </Button>
            }
          />
        </div>
      );
    }

    return (
      <div className={s.field}>
        <p className={s.title}>{title}</p>

        <div className={s.row}>
          <span className={s.value}>{value || t('common.notSet')}</span>

          <Button
            className={s['edit-btn']}
            onClick={handleStartEdit}
            aria-label={title}
          >
            <Edit />
          </Button>
        </div>
      </div>
    );
  },
);
ContactField.displayName = 'ContactField';
